import React from "react";

import Typography from "@mui/material/Typography";

import { IndividualDelivery, Hacobell } from "../api/individual_deliveries";
import { dateTimeProps, dateProps } from "../utils/locale";
import { useRecordContext } from "react-admin";

type Props = {
  label?: string;
  sortable?: boolean;
  headerClassName?: string;
  cellClassName?: string;
};

function formatDateTime(date: Date | null): string {
  if (date === null) {
    return "";
  }
  return date.toLocaleTimeString(dateTimeProps.locales, { ...dateTimeProps.options });
}

function formatEndDate(end: Date | null, start: Date | null): string {
  if (end === null) {
    return "";
  }
  if (start === null) {
    return formatDateTime(end);
  }
  const isSameDate =
    end.getFullYear() === start.getFullYear() &&
    end.getMonth() === start.getMonth() &&
    end.getDate() === start.getDate();
  if (!isSameDate) {
    return formatDateTime(end);
  }
  return end.toLocaleTimeString(dateTimeProps.locales, { hour: "numeric", minute: "numeric" });
}

function formatHacobellHandover(hacobell: Hacobell | null | undefined): string | null {
  if (!hacobell) {
    return null;
  }
  const { handover_timing_start, handover_timing_end } = hacobell;
  const startDate = handover_timing_start ? new Date(handover_timing_start) : null;
  const endDate = handover_timing_end ? new Date(handover_timing_end) : null;
  if (!startDate && !endDate) {
    return null;
  }
  const start = formatDateTime(startDate);
  const end = formatEndDate(endDate, startDate);

  if (handover_timing_start === handover_timing_end) {
    return start;
  } else {
    return `${start}〜${end}`;
  }
}

export const HandoverAtField: React.FC<Props> = () => {
  const record: IndividualDelivery = useRecordContext();
  if (!record) {
    return null;
  }
  const { delivery_type, hacobell, self_delivery } = record;
  let handoverAt: string | undefined | null;
  switch (delivery_type) {
    case "hacobell": {
      handoverAt = formatHacobellHandover(hacobell);
      break;
    }
    case "self_delivery": {
      handoverAt = self_delivery?.handover_date;
      break;
    }
    default: {
      break;
    }
  }

  if (!handoverAt) {
    return null;
  }

  if (delivery_type === "hacobell") {
    return <Typography variant="body2">{handoverAt}</Typography>;
  } else {
    const date = new Date(handoverAt);
    return (
      <Typography variant="body2">{date.toLocaleDateString(dateProps.locales, { ...dateProps.options })}</Typography>
    );
  }
};
